import {
  JobAlgoliaApiQueryFilters,
  VolopAlgoliaApiQueryFilters,
} from 'algolia-api/types/AlgoliaApiQueryFilters';
import { RouterButton } from 'components/Button/RouterButton';
import { Icon } from 'components/Icon';
import { BodyMicro } from 'components/Text/BodyMicro';
import { H6 } from 'components/Text/H6';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { getMainSearchRoutePathSwitch } from 'modules/main/helpers/searchRoute/getMainSearchRoutePathSwitch';
import { showModal } from 'modules/main/store/ducks/modal.actions';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { trackClicked } from 'utils/analytics/track/trackClicked';

import {
  CmsSocialSidebarContainer,
  CmsSocialSidebarNewsletterButtonContainer,
  CmsSocialSidebarRecentJobsContainer,
  CmsSocialSidebarShareButtonContainer,
  CmsSocialSidebarTooltipContainer,
} from './CmsSocialSidebar.styles';

type Props = {
  listingSearchType: 'JOB' | 'VOLOP';
  hasTableOfContents: boolean;
  hasSocialShare: boolean;
  subscribe: () => Promise<void>;
  canSubscribe: boolean;
  isSubscribed: boolean;
  shareUrl: Record<UserLocale, string>;
  subsiteSubscribeText: string;
};

export function CmsSocialSidebar({
  canSubscribe,
  isSubscribed,
  subscribe,
  listingSearchType,
  hasTableOfContents,
  hasSocialShare,
  shareUrl,
  subsiteSubscribeText,
}: Props) {
  const dispatch = useMainDispatch();

  const handleShare = () => {
    dispatch(
      showModal('SHARE_LISTING', {
        listing: { type: 'URL' },
        listingType: 'URL',
        messageOverride: getText('Check out this article'),
        shareUrl,
      }),
    );
  };

  if (!canSubscribe && !hasSocialShare) {
    return null;
  }

  const searchListingMap = {
    JOB: {
      headline: getText('Land Your Dream Job'),
      description: getText(
        'Careers that help you move from intention to action',
      ),
      buttonText: getText('Search Jobs'),
      url: getMainSearchRoutePathSwitch({
        q: '',
        lang: CURRENT_LOCALE,
        radius: null,
        filters: {
          type: 'JOB',
        } as JobAlgoliaApiQueryFilters,
      }),
    },
    VOLOP: {
      headline: getText('Make a Difference'),
      description: getText(
        'Search for volunteer opportunities in your community',
      ),
      buttonText: getText('Search'),
      url: getMainSearchRoutePathSwitch({
        q: '',
        lang: CURRENT_LOCALE,
        radius: null,
        filters: {
          type: 'VOLOP',
          actionType: ['VOLOP'],
        } as VolopAlgoliaApiQueryFilters,
      }),
    },
  };

  return (
    <CmsSocialSidebarContainer $hasTableOfContents={hasTableOfContents}>
      <CmsSocialSidebarRecentJobsContainer>
        <H6 lineHeight={1} mb="16px">
          {searchListingMap[listingSearchType].headline}
        </H6>
        <BodyMicro lineHeight={1.2} mb="16px">
          {searchListingMap[listingSearchType].description}
        </BodyMicro>
        <RouterButton
          data-qa-id="sidebar-recent-listings-button"
          onClick={() =>
            trackClicked(searchListingMap[listingSearchType].buttonText, {
              context: 'CMS Page',
            })
          }
          to={searchListingMap[listingSearchType].url}
          state={{ filterType: listingSearchType }}
          variant="primary"
          size="small"
        >
          {searchListingMap[listingSearchType].buttonText}
        </RouterButton>
      </CmsSocialSidebarRecentJobsContainer>
      <CmsSocialSidebarTooltipContainer>
        {canSubscribe ? (
          <Tooltip
            variant="filled"
            content={
              isSubscribed
                ? getText('You are now subscribed!')
                : subsiteSubscribeText
            }
          >
            <CmsSocialSidebarNewsletterButtonContainer
              type="button"
              onClick={subscribe}
              data-qa-id="sidebar-subscribe-to-subsite"
              disabled={isSubscribed}
              aria-label={getText('Subscribe')}
            >
              <Icon name="newsletter" size={30} />
            </CmsSocialSidebarNewsletterButtonContainer>
          </Tooltip>
        ) : null}
        {hasSocialShare ? (
          <Tooltip variant="filled" content={getText('Share')}>
            <CmsSocialSidebarShareButtonContainer
              type="button"
              onClick={handleShare}
              data-qa-id="sidebar-social-share-button"
              aria-label={getText('Share')}
            >
              <Icon name="share" size={24} />
            </CmsSocialSidebarShareButtonContainer>
          </Tooltip>
        ) : null}
      </CmsSocialSidebarTooltipContainer>
    </CmsSocialSidebarContainer>
  );
}
